/**
 * MODULE: domain/sales-intelligence/frontend/components/SalesIntelligenceModal.tsx
 * PURPOSE: Reusable modal wrapper for Sales Intelligence chat interface
 *
 * DEPENDENCIES:
 *   - flowbite-react: Modal, ModalHeader, ModalBody
 *   - @heroicons/react: BoltIcon
 *   - lucide-react: X
 *   - ./SalesChatAssistant: Main chat component
 *
 * PUBLIC API:
 *   - SalesIntelligenceModal: Modal component
 *   - SalesIntelligenceModalProps: Props interface
 *
 * SIDE EFFECTS:
 *   - Renders modal overlay
 *
 * AGENT SAFETY: SAFE
 * FILE SIZE: 75 / 200 lines
 *
 * See: /README.md | /domain/sales-intelligence/README.md
 */

import React from 'react';
import { Modal, ModalHeader, ModalBody } from 'flowbite-react';
import { BoltIcon } from '@heroicons/react/24/outline';
import { X } from 'lucide-react';
import { SalesChatAssistant } from './SalesChatAssistant';

export interface SalesIntelligenceModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Called when modal open state changes (e.g. close requested) */
  onOpenChange: (open: boolean) => void;
  /** Optional company name to prefill in the chat */
  prefillCompany?: string | null;
  /** Called when user saves research to account (e.g. close modal, refetch) */
  onSaveToAccount?: () => void;
}

/**
 * SalesIntelligenceModal - Modal wrapper for the Sales Intelligence chat interface.
 * Use from anywhere to open Sales Intelligence in an overlay (e.g. Accounts page, dashboard).
 *
 * @example
 * const [isOpen, setIsOpen] = useState(false);
 * <button onClick={() => setIsOpen(true)}>Open Sales Intelligence</button>
 * <SalesIntelligenceModal open={isOpen} onOpenChange={setIsOpen} />
 */
export function SalesIntelligenceModal({
  open,
  onOpenChange,
  prefillCompany = null,
  onSaveToAccount,
}: SalesIntelligenceModalProps) {
  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSaveToAccount = () => {
    onSaveToAccount?.();
    onOpenChange(false);
  };

  return (
    <Modal
      show={open}
      onClose={handleClose}
      size="7xl"
      className="!h-[90vh]"
    >
      <ModalHeader>
        <div className="flex items-center justify-between w-full pr-2">
          <div className="flex items-center gap-2">
            <BoltIcon className="w-5 h-5 text-blue-600" />
            <span>Sales Intelligence Research</span>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
      </ModalHeader>
      <ModalBody className="!p-0 !overflow-hidden">
        <div className="h-[calc(90vh-130px)]">
          <SalesChatAssistant
            prefillCompany={prefillCompany ?? undefined}
            onSaveToAccount={onSaveToAccount ? handleSaveToAccount : undefined}
          />
        </div>
      </ModalBody>
    </Modal>
  );
}
